import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { OrgAdminSidebarComponent } from '../sidebar/org-admin-sidebar.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { AuthApiService } from '@angular-workspace/services';
import { DataProviderEntityFormComponent, DataProviderEntityFormData } from '../../shared/components/data-provider-entity-form/data-provider-entity-form.component';

@Component({
  selector: 'app-org-admin-data-provider-entity',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    OrgAdminSidebarComponent,
    PageHeaderComponent,
    DataProviderEntityFormComponent,
  ],
  templateUrl: './data-provider-entity.component.html',
  styleUrl: './data-provider-entity.component.scss',
})
export class OrgAdminDataProviderEntityComponent implements OnInit {
  // Form data for shared component
  formData: DataProviderEntityFormData = {};
  organisationStatus: 'pending' | 'granted' | null = null;

  constructor(
    private authApiService: AuthApiService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadDataProviderEntityData();
  }

  async loadDataProviderEntityData(): Promise<void> {
    try {
      const userData = await this.authApiService.getUserInfo();
      
      if (userData && userData['result']) {
        const result = userData['result'];
        const organisation = result['organisation'] || result['organization'] || {};
        
        // Extract organisation status
        if (organisation && organisation.status) {
          this.organisationStatus = organisation.status === 'pending' || organisation.status === 'granted' ? organisation.status : null;
        }
        
        // Helper function to format entity type for display
        const formatEntityType = (value: string): string => {
          if (!value) return '';
          // Convert to PascalCase for display
          return value
            .split(/[_\s-]/)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        };

        // Helper function to format entity sector for display
        const formatEntitySector = (value: string): string => {
          if (!value) return '';
          // Convert to PascalCase for display
          return value
            .split(/[_\s-]/)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        };

        // Extract document file names and paths if available (handle both snake_case and camelCase)
        const documentFiles: { [key: string]: string } = {};
        const documentPaths: { [key: string]: string } = {};
        
        const certPath = organisation['certificatePath'] || organisation['certificate_path'] || '';
        if (certPath) {
          documentFiles['incorporation'] = certPath.split('/').pop() || certPath;
          documentPaths['incorporation'] = certPath; // Store full path for PDF viewing
        }
        
        const panPath = organisation['pancardPath'] || organisation['pancard_path'] || '';
        if (panPath) {
          documentFiles['panCard'] = panPath.split('/').pop() || panPath;
          documentPaths['panCard'] = panPath; // Store full path for PDF viewing
        }
        
        const authPath = organisation['orgDocuments'] || organisation['org_documents'] || organisation['letterOfAuthorization'] || organisation['letter_of_authorization'] || '';
        if (authPath) {
          documentFiles['authorization'] = authPath.split('/').pop() || authPath;
          documentPaths['authorization'] = authPath; // Store full path for PDF viewing
        }
        
        const relPath = organisation['relevantDocPath'] || organisation['relevant_doc_path'] || '';
        if (relPath) {
          documentFiles['others'] = relPath.split('/').pop() || relPath;
          documentPaths['others'] = relPath; // Store full path for PDF viewing
        }

        // Prepare form data for shared component
        this.formData = {
          // User Details
          firstName: result['givenName'] || result['givenName'] || '',
          lastName: result['familyName'] || result['familyName'] || '',
          registeredEmail: result['email'] || '',
          jobTitle: result['jobTitle'] || organisation['jobTitle'] || result['job_title'] || organisation['job_title'] || '',
          employeeId: result['empId'] || result['employeeId'] || result['employee_id'] || organisation['empId'] || organisation['employeeId'] || organisation['employee_id'] || '',
          
          // Entity Details from organisation object
          entityType: formatEntityType(organisation['entityType'] || organisation['entity_type'] || organisation['type'] || ''),
          entitySector: formatEntitySector(organisation['orgSector'] || organisation['org_sector'] || organisation['sector'] || organisation['entitySector'] || organisation['entity_sector'] || ''),
          entityName: organisation['organisationName'] || organisation['organizationName'] || organisation['organisation_name'] || organisation['organization_name'] || organisation['orgName'] || organisation['org_name'] || organisation['name'] || '',
          websiteUrl: organisation['websiteLink'] || organisation['website_link'] || organisation['websiteUrl'] || organisation['website_url'] || organisation['website'] || organisation['url'] || '',
          address: organisation['address'] || organisation['fullAddress'] || organisation['full_address'] || '',
          
          // Document files
          documentFiles: documentFiles,
          // Document paths for PDF viewing
          documentPaths: documentPaths,
        };
      }
    } catch (error) {
      console.error('Error loading organisation data:', error);
    }
  }
}

